import {Injectable}               from '@angular/core';
import {Validators}               from '@angular/forms';
import {
  distinctUntilChanged,
  filter,
  MonoTypeOperatorFunction,
  Observable
}                                 from 'rxjs';
import {map}                      from 'rxjs/operators';
import {GetRoundNumericPipe}      from '../../../ap-utils';
import {ApDynformsConfigFieldset} from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigNumerictextbox
}                                 from '../../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {ApDynformsValidator}      from '../../../ap-dynforms/ap-dynforms-validator';
import {AgriportConstantsEnum}    from '../../../ap-interface/enums/ap-agriport-constants.enum';
import {AgriportConstantsService} from '../../../services/common/agriport-constants.service';
import {
  ApDynGridColumnConfigBase
}                                 from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                                 from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {ApDynformsConfigTextbox}  from '../../../ap-dynforms/config/ap-dynforms-config-textbox';
import {SettingsStore}            from '../../../stores/base-data/settings.store';
import {ApNutrientService}        from '../../../ap-utils/service/ap-nutrient.service';
import {ApElementType}            from '../../../ap-interface/enums/ap-elements-type.enum';
import {FertilizerStore}          from '../../../stores/base-data/fertilizer.store';
import {ApTranslationService}     from '../../../ap-utils/service/ap-translation.service';
import {GetRoundNumericService}   from '../../../ap-utils/service/get-round-numeric.service';
import {ApDynformsComponent}      from '../../../ap-dynforms/ap-dynforms.component';

@Injectable()
export class UniformConfig {
  private _minNutrient = 1;
  private _maxNutrient = 250;

  constructor(private settingsStore: SettingsStore,
              private fertilizerStore: FertilizerStore,
              private nutrientService: ApNutrientService,
              private translationService: ApTranslationService,
              private roundNumericService: GetRoundNumericService,
              private agriportConstantsService: AgriportConstantsService,
              private roundNumericPipe: GetRoundNumericPipe) {
  }

  /**
   * Constructs and returns a dynamic form fieldset configuration for nutrient-related pages.
   * @param dynForm - The parent dynamic form component.
   * @param formValues - The initial values for form fields, typically provided as an object with key-value pairs.
   * @param formValues$ - An observable stream of form field values used to dynamically update field properties.
   * @param totalArea - The total area value used for calculations such as product amount per hectare.
   * @param isModalField - Specifies if the form is being used in a modal context to apply specific settings like max width.
   * @return A configuration object defining the structure and behavior of the dynamic form fieldset.
   * @public
   */
  public formBuilder(dynForm: ApDynformsComponent | undefined, formValues: any, formValues$: Observable<any>,
                     totalArea: number, isModalField: boolean): ApDynformsConfigFieldset {
    const decimalsCount = this.settingsStore.FirstSetting.DigitsAfterDecimalPoint;
    return new ApDynformsConfigFieldset({
      legend: 'Nutrients_Pages_Popups__Amount',
      key: 'Amount',
      useMaxWidth: isModalField,
      config: [
        new ApDynformsConfigNumerictextbox({
          key: 'ECStage',
          label: 'Global__ECStage',
          value: formValues['ECStage'],
          min: 0,
          max: +this.agriportConstantsService.GetConstant(AgriportConstantsEnum.EcMax),
          decimals: 0,
          format: 'n0',
          step: 1,
          validators: [
            new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
          ]
        }),
        new ApDynformsConfigTextbox({
          key: 'Constant',
          label: 'Global__NutrientN_Full_Unit',
          value: formValues['Constant'],
          value$: formValues$.pipe(map(values => {
            const productId = this.getProductId(isModalField, formValues, values);
            const nutrientAmount = this.getNutrientValueFromGoods(values['ProductAmountPerHa'], productId);
            return this.roundNumericService.roundAsNumber(nutrientAmount, decimalsCount);
          })),
          informational: true
        }),
        new ApDynformsConfigNumerictextbox({
          key: 'ProductAmount',
          label: 'Global__Sum_Goods',
          label$: formValues$.pipe(
            this.filterProductId(isModalField, formValues),
            map(productId => this.productAmountLabel(productId, false))
          ),
          value: formValues['ProductAmount'],
          value$: formValues$.pipe(
            filter(() => dynForm?.getLastFocusedControl() !== 'ProductAmount'),
            map(values => values['ProductAmountPerHa']),
            distinctUntilChanged(),
            map(productAmountPerHa => {
              const productAmount = this.productAmountTotalCalculation(productAmountPerHa, totalArea);
              return this.roundNumericService.roundAsNumber(productAmount, 0);
            }),
            distinctUntilChanged()
          ),
          min: formValues$.pipe(
            this.filterProductId(isModalField, formValues),
            map(productId => this.getProductAmountTotal(productId, this._minNutrient, totalArea))
          ),
          max: formValues$.pipe(
            this.filterProductId(isModalField, formValues),
            map(productId => this.getProductAmountTotal(productId, this._maxNutrient, totalArea))
          ),
          decimals: 0,
          format: 'n0',
          step: 1,
          informational: !dynForm,
          validators: [
            new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
          ]
        }),
        new ApDynformsConfigNumerictextbox({
          key: 'ProductAmountPerHa',
          label: 'Global__Product',
          label$: formValues$.pipe(
            this.filterProductId(isModalField, formValues),
            map(productId => this.productAmountLabel(productId, true))
          ),
          value: this.productAmountPerHaCalculation(formValues['ProductAmount'], totalArea),
          value$: formValues$.pipe(
            filter(() => dynForm?.getLastFocusedControl() !== 'ProductAmountPerHa' && !isModalField),
            map(values => values['ProductAmount']),
            distinctUntilChanged(),
            map(productAmount => {
              const productAmountPerHa = this.productAmountPerHaCalculation(productAmount, totalArea);
              return this.roundNumericService.roundAsNumber(productAmountPerHa, decimalsCount);
            }),
            distinctUntilChanged()
          ),
          min: formValues$.pipe(
            this.filterProductId(isModalField, formValues),
            map(productId => this.getProductAmountPerHa(productId, this._minNutrient))
          ),
          max: formValues$.pipe(
            this.filterProductId(isModalField, formValues),
            map(productId => this.getProductAmountPerHa(productId, this._maxNutrient))
          ),
          decimals: decimalsCount,
          format: `n${decimalsCount}`,
          step: 1,
          validators: [
            new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
          ]
        })
      ]
    });
  }

  /**
   * Configures the grid layout and properties for a given product.
   * @param productId - The unique identifier of the product for which the grid configuration is being built.
   * @return An array of grid column configuration objects defining the properties, layout, and appearance of the grid columns.
   * @public
   */
  public gridBuilderConfig(productId: number): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'Global__NutrientN_Full_Unit',
        field: 'Constant',
        hide: true,
        columnLoading: 'ColumnLoading',
        width: 90,
        cssClass: 'right',
        filterType: FilterType.NUMBER,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [0]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        title: this.productAmountLabel(productId, true),
        field: 'ProductAmountPerHa',
        hide: false,
        columnLoading: 'ColumnLoading',
        width: 90,
        cssClass: 'right',
        filterType: FilterType.NUMBER,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [0]
        }]
      })
    ];
  }

  /**
   * Calculates the product amount per hectare based on the total product amount and the total area.
   * @param totalProductAmount The total amount of the product. If not provided or not a finite number, the result will be zero.
   * @param totalArea The total area in hectares. If not provided, not a finite number, or equal to zero, the result will be zero.
   * @return The calculated product amount per hectare. Returns zero if inputs are invalid or if either value is zero.
   * @public
   */
  public productAmountPerHaCalculation(totalProductAmount: number | undefined, totalArea: number | undefined): number {
    if (!Number.isFinite(totalProductAmount) || !Number.isFinite(totalArea)
      || totalArea === 0 || totalProductAmount === 0) {
      return 0;
    }
    return totalProductAmount / totalArea;
  }

  /**
   * Calculates the nutrient value from product amount based on the product ID and the amount per hectare.
   * @param productAmountPerHa - The amount of product per hectare.
   * @param productId - The ID of the product.
   * @return The calculated nutrient value. Returns 0 if the input is invalid or the product ID is 0.
   * @public
   */
  public getNutrientValueFromGoods(productAmountPerHa: number | undefined, productId: number | undefined): number {
    if (!Number.isFinite(productId) || !Number.isFinite(productAmountPerHa) || productId === 0) {
      return 0;
    }
    return this.nutrientService.convertGoodsToNutrient(productId, ApElementType.N, productAmountPerHa);
  }

  /**
   * Calculates the total product amount based on the given product ID, element amount, and total area.
   * @param productId - The ID of the product. If undefined or invalid, the result will be 0.
   * @param elementAmount - The amount of the element to be converted. Must be a finite number greater than 0.
   * @param totalArea - The total area for the calculation. Must be a finite number greater than 0.
   * @return The calculated total product amount. Returns 0 if the input parameters are invalid.
   * @private
   */
  private getProductAmountTotal(productId: number | undefined, elementAmount: number, totalArea: number): number {
    if (!Number.isFinite(elementAmount) || !Number.isFinite(totalArea) || !Number.isFinite(productId)
      || elementAmount === 0 || totalArea === 0 || productId === 0) {
      return 0;
    }
    const productAmountPerHa = this.nutrientService.convertNutrientToGoods(productId, ApElementType.N, elementAmount);
    const result = productAmountPerHa * totalArea;
    return this.roundNumericService.roundAsNumber(result, 0);
  }

  /**
   * Calculates the product amount per hectare based on the given product ID and element amount.
   * @param productId - The ID of the product to be used for the calculation. If undefined, the method will return 0.
   * @param elementAmount - The amount of the element to be converted into product quantity.
   * @return The calculated product amount per hectare. Returns 0 if the input values are invalid or 0.
   * @private
   */
  private getProductAmountPerHa(productId: number | undefined, elementAmount: number): number {
    if (!Number.isFinite(elementAmount) || !Number.isFinite(productId)
      || elementAmount === 0 || productId === 0) {
      return 0;
    }
    const productPerHa = this.nutrientService.convertNutrientToGoods(productId, ApElementType.N, elementAmount);
    return this.roundNumericService.roundAsNumber(productPerHa, this.settingsStore.FirstSetting.DigitsAfterDecimalPoint);
  }

  /**
   * Calculates the total product amount based on the product amount per hectare and the total area.
   * @param productAmountPerHa - The product amount per hectare. Must be a finite number.
   * @param totalArea - The total area in hectares. Must be a finite number.
   * @return The total product amount. Returns 0 if either parameter is not a finite number or if either is 0.
   * @private
   */
  private productAmountTotalCalculation(productAmountPerHa: number | undefined, totalArea: number | undefined): number {
    if (!Number.isFinite(productAmountPerHa) || !Number.isFinite(totalArea)
      || totalArea === 0 || productAmountPerHa === 0) {
      return 0;
    }
    return productAmountPerHa * totalArea;
  }

  /**
   * Generates a label for the product amount based on the specified product ID and usage information.
   * @param productId - The ID of the product to retrieve the label for. If undefined, the fallback label will be used.
   * @param usePerHa - Indicates whether the unit should be displayed per hectare.
   * @return The translated label for the product amount, optionally including the unit and usage information.
   * @private
   */
  private productAmountLabel(productId: number | undefined, usePerHa: boolean): string {
    const fertilizer = this.fertilizerStore.getFertilizer(productId);
    const unitKey = fertilizer?.Unit?.Key ?? fertilizer?.Unit?.BaseUnit?.Key;
    if (!unitKey) {
      return 'Global__Product';
    }
    const unit = this.translationService.translate(unitKey);
    const unitInfo = usePerHa
      ? `${unit}/${this.translationService.translate('Global__Unit_ha')}`
      : unit;
    return `${this.translationService.translate('Global__Product')} [${unitInfo}]`;
  }

  /**
   * Filters the product ID from the input observable, ensuring it is a finite number
   * greater than 0 and emits only distinct values.
   * @template T The type of the input observable data.
   * @return A function that returns an observable emitting
   * valid, distinct product IDs.
   * @private
   */
  private filterProductId<T>(isModalDialog: boolean, initFormValues: any): MonoTypeOperatorFunction<T> {
    return (source: Observable<T>) => source.pipe(
      map(formValues => this.getProductId(isModalDialog, initFormValues, formValues) as any),
      filter(productId => Number.isFinite(productId) && productId > 0),
      distinctUntilChanged()
    );
  }

  /**
   * Retrieves the product ID based on the input parameters.
   * @param isModalDialog - Specifies whether the product ID should be retrieved from the modal dialog's initial form values.
   * @param initFormValues - The initial form values, used when the modal dialog condition is true.
   * @param formValues - The form values, used when the modal dialog condition is false.
   * @return The product ID retrieved from the appropriate source.
   * @private
   */
  private getProductId(isModalDialog: boolean, initFormValues: any, formValues: any): number {
    return isModalDialog ? initFormValues['Product'] : formValues['Product'];
  }
}
